import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	achievementsGlow,
	FILTER_ACTIVE_FILL,
	FILTER_FILL,
} from './achievementsTheme';
import { AchievementCategoryOption } from './achievementsViewModel';

/**
 * Figma "Dropdown / Categories" (4395:3713), shown in section "05 · Components & tokens"
 * (4488:47976). A 160-wide menu on the same plate as a resting filter chip, with a 6 inset, 2
 * between rows and a heavy drop shadow. Rows are "Dropdown Item / Category" (4395:3712): 32 tall,
 * 12 of side padding, radius 6, and the selected one wears the active red plate the chips use.
 *
 * The file draws the menu only as a component, never open on an artboard, so the screen anchors
 * it under the Categories chip it belongs to.
 */
export const DROPDOWN_WIDTH = 160;
const PADDING = 6;
const ROW_GAP = 2;
const ROW_HEIGHT = 32;
const ROW_RADIUS = 6;
const RADIUS = 12;

type Props = {
	categories: AchievementCategoryOption[];
	value?: AchievementCategoryOption;
	/** Choosing the row that is already applied clears the filter. */
	onChange: (category?: AchievementCategoryOption) => void;
	scale: number;
};

export const CategoriesDropdown = ({ categories, value, onChange, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const plateId = useSvgId('achievementDropdown');
	const rowId = useSvgId('achievementDropdownRow');

	return (
		<View style={styles.menu}>
			<View pointerEvents='none' style={styles.plate}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={plateId} x1='0' y1='0' x2='0' y2='1'>
							{FILTER_FILL.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${plateId})`} />
				</Svg>
			</View>

			{categories.map(category => {
				const isSelected = category.category === value?.category;

				return (
					<Pressable
						key={category.category}
						accessibilityRole='menuitem'
						accessibilityState={{ selected: isSelected }}
						onPress={() => onChange(isSelected ? undefined : category)}
						style={[styles.row, isSelected && styles.rowSelected]}
					>
						{isSelected && (
							<View pointerEvents='none' style={styles.rowPlate}>
								<Svg width='100%' height='100%'>
									<Defs>
										<LinearGradient id={rowId} x1='0' y1='0' x2='0' y2='1'>
											{FILTER_ACTIVE_FILL.map(([offset, color]) => (
												<Stop key={offset} offset={offset} stopColor={color} />
											))}
										</LinearGradient>
									</Defs>
									<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${rowId})`} />
								</Svg>
							</View>
						)}
						<Text
							numberOfLines={1}
							style={[styles.label, isSelected ? styles.labelSelected : styles.labelRest]}
						>
							{t(category.nameKey)}
						</Text>
					</Pressable>
				);
			})}
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		menu: {
			width: DROPDOWN_WIDTH * scale,
			padding: PADDING * scale,
			gap: ROW_GAP * scale,
			borderRadius: RADIUS * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.border,
			...achievementsGlow.menu,
		},
		// Clipped on its own so the menu's shadow is not cut off with it.
		plate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS * scale,
			overflow: 'hidden',
		},
		row: {
			height: ROW_HEIGHT * scale,
			justifyContent: 'center',
			paddingHorizontal: 12 * scale,
			borderRadius: ROW_RADIUS * scale,
		},
		rowSelected: {
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.borderActive,
			...achievementsGlow.filterActive,
		},
		rowPlate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: ROW_RADIUS * scale,
			overflow: 'hidden',
		},
		// Figma "DS/Label/M": Sofia Sans SemiBold 12, line-height 1.2.
		label: {
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.2 * scale,
		},
		labelRest: {
			color: achievementsColors.secondary,
		},
		labelSelected: {
			color: achievementsColors.primary,
		},
	});

export default CategoriesDropdown;
